import { toast } from "react-hot-toast"
import type { ValidationErrors } from "../types/application.types"

// Real-time validation functions for form fields
export const validateDate = (selectedDate: string) => {
  if (!selectedDate) return { isValid: true }

  const interviewDate = new Date(selectedDate)
  const currentDate = new Date()
  const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
  const tenDaysAgo = new Date(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() - 10 * 24 * 60 * 60 * 1000);
  const selectedDateOnly = new Date(interviewDate.getFullYear(), interviewDate.getMonth(), interviewDate.getDate())

  if (selectedDateOnly < tenDaysAgo) { //changed to 10 days before for adding data after migrartion
    toast.error("⚠️ Interview date cannot be in the past. Please select a future date.", {
      duration: 4000,
      icon: "📅",
      style: {
        background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
        border: "1px solid #ef4444",
        borderLeft: "4px solid #dc2626",
      },
    })
    return { isValid: false }
  }

  return { isValid: true }
}

export const validateStartTime = (
  startTime: string,
  scheduledDate: string,
  endTime: string,
  setValidationErrors: (fn: (prev: ValidationErrors) => ValidationErrors) => void,
) => {
  if (!startTime || !scheduledDate) {
    setValidationErrors((prev) => ({ ...prev, startTime: false }))
    return { isValid: true }
  }

  const selectedDate = new Date(scheduledDate)
  const today = new Date()
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())

  // If the selected date is today, check if start time is in the future
  if (selectedDateOnly.getTime() === todayOnly.getTime()) {
    const now = new Date()
    const [startHour, startMinute] = startTime.split(":").map(Number)
    const startDateTime = new Date(selectedDate)
    startDateTime.setHours(startHour, startMinute, 0, 0)

    if (startDateTime <= now) {
      setValidationErrors((prev) => ({ ...prev, startTime: true }))
      toast.error("⏰ Start time cannot be in the past. Please select a future time.", {
        duration: 4000,
        icon: "🕐",
        style: {
          background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          border: "1px solid #f59e0b",
          borderLeft: "4px solid #d97706",
        },
      })
      return { isValid: false }
    }
  }

  // Validate against end time if it exists
  if (endTime) {
    const timeValidation = validateTimeRange(startTime, endTime)
    if (!timeValidation.isValid && timeValidation.message) {
      setValidationErrors((prev) => ({ ...prev, startTime: true, endTime: true }))
      toast.error(`⏱️ ${timeValidation.message}`, {
        duration: 4000,
        icon: "⚠️",
        style: {
          background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          border: "1px solid #f59e0b",
          borderLeft: "4px solid #d97706",
        },
      })
      return { isValid: false }
    }
  }

  setValidationErrors((prev) => ({ ...prev, startTime: false }))
  return { isValid: true }
}

export const validateEndTime = (
  endTime: string,
  startTime: string,
  setValidationErrors: (fn: (prev: ValidationErrors) => ValidationErrors) => void,
) => {
  if (!endTime || !startTime) {
    setValidationErrors((prev) => ({ ...prev, endTime: false }))
    return { isValid: true }
  }

  const timeValidation = validateTimeRange(startTime, endTime)
  if (!timeValidation.isValid && timeValidation.message) {
    setValidationErrors((prev) => ({ ...prev, startTime: true, endTime: true }))
    toast.error(`⏱️ ${timeValidation.message}`, {
      duration: 4000,
      icon: "⚠️",
      style: {
        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
        border: "1px solid #f59e0b",
        borderLeft: "4px solid #d97706",
      },
    })
    return { isValid: false }
  }

  setValidationErrors((prev) => ({ ...prev, startTime: false, endTime: false }))
  return { isValid: true }
}

// Utility function to validate time range
function validateTimeRange(startTime: string, endTime: string): { isValid: boolean; message?: string } {
  if (!startTime || !endTime) {
    return { isValid: true } // Skip validation if times are empty
  }

  try {
    const [startHour, startMinute] = startTime.split(":").map(Number)
    const [endHour, endMinute] = endTime.split(":").map(Number)

    // Check for valid time format
    if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
      return { isValid: false, message: "Invalid time format" }
    }

    const startTimeInMinutes = startHour * 60 + startMinute
    const endTimeInMinutes = endHour * 60 + endMinute

    if (startTimeInMinutes >= endTimeInMinutes) {
      return {
        isValid: false,
        // message: "End time must be after start time. Please adjust the timing.",
      }
    }

    const durationMinutes = endTimeInMinutes - startTimeInMinutes

    // Check maximum duration (8 hours)
    if (durationMinutes > 480) {
      return {
        isValid: false,
        message: "Interview duration cannot exceed 8 hours. Please adjust the timing.",
      }
    }

    return { isValid: true }
  } catch (error) {
    return { isValid: false, message: "Invalid time format" }
  }
}
